<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <component
      :is="componentType"
      class="tile"
      :class="{ 'tile-link': linkHref }"
      v-bind="propTypes"
    >
      <media-display
        :sc-image-field="imageField"
        :image-desktop-source="imageSrc"
        :fraction="fraction"
      />
      <div v-if="showDescription" class="tile-description">
        <heading
          v-if="hasLargeHeader"
          class="large-header"
          desktopSize="size-3"
          mobileSize="size-4"
          :tagField="headingSizeField"
          :textField="largeHeaderField"
        />
        <heading
          v-if="hasTitle"
          class="title"
          desktopSize="size-3"
          mobileSize="size-4"
          :tagField="titleHeadingSizeField"
          :textField="titleField"
        />
        <sc-text
          v-if="hasTag"
          class="tag"
          tag="p"
          :field="tagField"
        />
      </div>
      <div v-if="linkHref" class="cta">
        <sh-button
          tabindex="-1"
          :color="buttonColor"
          :[setStyle]=true
        >
          {{ ctaField.text }}
        </sh-button>
      </div>
    </component>
  </sc-render>
</template>

<script>
import { Image, Text } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'

import CTA from '~/constants/appearance'
import ShLink from '~/components/sh-link'
import mapFields from '~/helpers/map-fields'
import { decreaseTagValue } from '~/helpers/decrease-tag-value'
import getButtonStyle from '~/helpers/get-button-style'

import ScRender from '../sc-render'
import Heading from '../heading'
import MediaDisplay from '~/components/sitecore/media-display'
import fraction from '~/constants/fraction'
import ThemeMixin from '~/mixins/theme'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'TileImage',

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    ScImage: Image,
    ScRender,
    ShLink,
    ScText: Text,
    Heading,
    MediaDisplay
  },

  mixins: [ThemeMixin],

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      imageField: 'image',
      imageValue: 'image.value',
      ctaField: 'cta.value',
      ctaAppearance: 'ctaAppearance.value',
      linkHref: 'cta.value.href',
      linkType: 'cta.value.linktype',
      linkTarget: 'cta.value.target',
      largeHeaderField: 'largeHeader',
      largeHeaderValue: 'largeHeader.value',
      titleField: 'title',
      titleValue: 'title.value',
      tagField: 'tag',
      tagValue: 'tag.value',
      headingSizeField: 'headingSize'
    }),

    titleHeadingSizeField () {
      return decreaseTagValue(this.headingSizeField)
    },

    componentType () {
      if (!this.linkHref || this.isEditMode) {
        return 'div'
      }

      return 'sh-link'
    },

    propTypes () {
      if (this.componentType === 'div') {
        return {}
      }

      return {
        tabindex: '0',
        to: this.linkHref,
        target: this.linkTarget
      }
    },

    buttonColor () {
      return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
    },

    setStyle () {
      return getButtonStyle(this.ctaAppearance)
    },

    hasLargeHeader () {
      return this.isEditMode || this.largeHeaderValue
    },

    hasTitle () {
      return this.isEditMode || this.titleValue
    },

    hasTag () {
      return this.isEditMode || this.tagValue
    },

    showDescription () {
      return this.isEditMode
        || this.hasLargeHeader
        || this.hasTitle
        || this.hasTag
    },

    imageSrc () {
      if (!this.imageField) {
        return ''
      }

      return this.imageValue[this.aspectRatioKey] || this.imageValue.src
    },

    // calculate images aspect ratio due to current cloudinary requirements
    aspectRatioKey () {
      const w = this.imageValue.width
      const h = this.imageValue.height

      if (!w || !h) return false

      function calculateDimension (a, b) {
        return (b === 0) ? a : calculateDimension(b, a % b)
      }

      const dimension = calculateDimension(w, h)
      const aspectRatioWidth = w / dimension
      const aspectRatioHeight = h / dimension
      return `cloudinary_${aspectRatioWidth}x${aspectRatioHeight}_fill_crop`
    },

    fraction () {
      return fraction.twoFr // see styles for layout-item width in Tile Image Container
    }
  }
}
</script>

<style lang="scss" scoped>

.tile {
  display: block;

  .tile-description {
    padding: $space-060 $space-050 0;
    @include stack-space($space-040);

    @include tablet () {
      padding-left: 0;
      padding-right: 0;
    }

    .large-header {
      @include font('080');
      @include stack-space($space-030);

      @include tablet () {
        @include font('090');
      }
    }

    .title {
      @include font('070');
      @include stack-space($space-030);
    }

    .tag {
      @include font('040');

      color: $color_gray;
    }
  }

  .cta {
    padding: 0 $space-050;

    @include tablet {
      padding: 0;
    }
  }
}

.tile-link {
  text-decoration: none;
  cursor: pointer;
}
</style>
